import api from "@/utils/axios";
import { defineStore } from "pinia";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: null as string | null,
    user: null as Record<string, any> | null,
    permissions: [] as string[], // ✅ explicitly typed
    permissionsLoaded: false, // true once a fetch has resolved (success or failure) this session
    permissionsPromise: null as Promise<void> | null, // dedupe concurrent fetches
  }),

  actions: {
    setToken(token: string) {
      this.token = token;
    },
    setUser(user: Record<string, any>) {
      this.user = user;
    },
    clearAuth() {
      this.token = null;
      this.user = null;
      this.permissions = []
      this.permissionsLoaded = false
    },

    setPermissions(permissions: Record<string, any>) {
      this.permissions = Object.keys(permissions).filter(key => permissions[key])
      this.permissionsLoaded = true
    },

    async fetchPermissions() {
      // dedupe: if a fetch is already in flight, reuse it instead of firing another request
      if (this.permissionsPromise) return this.permissionsPromise;

      this.permissionsPromise = (async () => {
        try {
          if (this.user) {
            const { data } = await api.get(`/api/auth/get_perms_user/${this.user?.id}`, {
              headers: {
                Authorization: `Bearer ${this.token}`
              }
            })
            this.permissions = data || []
          }
        } catch (error) {
          // keep the last known-good permissions on a transient failure instead of
          // wiping them out (was hiding every permission-gated sidebar item on any
          // network hiccup/background refresh failure)
          console.error('Failed to fetch permissions:', error)
        } finally {
          this.permissionsLoaded = true
          this.permissionsPromise = null
        }
      })();

      return this.permissionsPromise;
    },

    // call before checking permissions on first load when we have a token
    // but haven't fetched/restored permissions yet this session
    async ensurePermissions() {
      if (this.permissionsLoaded || this.permissions.length > 0) return;
      await this.fetchPermissions();
    },

    can(key : string) {
      return this.permissions.includes(key)
    }
  },

  persist: {
    pick: ["token", "user", "permissions"], // don't persist transient loading flags
  },
});
